import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Unstable_Grid2 as Grid,
  Link,
  Skeleton,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { has } from 'lodash';
import toast from 'react-hot-toast';
import { useParams } from 'react-router';
import { FileArrowDown, UploadSimple, X } from '@phosphor-icons/react';

import type { Meeting, MeetingFile } from '@/types/meeting';
import { config } from '@/config';
import { getPresignedUploadUrl, getS3PresignedUrlFromPath, uploadFileToPresignedUrl } from '@/utils/s3';

import MeetingTiptap from './MeetingTiptap';

function isPdfFileName(name: string): boolean {
  return /\.pdf$/i.test(name ?? '');
}

function isImageFileName(name: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name ?? '');
}

function MeetingOverview() {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [heldOn, setHeldOn] = useState<Date | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewFile, setPreviewFile] = useState<MeetingFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { meetingId } = useParams();

  const refetchMeeting = useCallback(async () => {
    if (!meetingId) return;
    try {
      const response = await axios.get(`${config.site.api}/api/v1/meetings/${meetingId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = response.data;
      setMeeting(data ?? ({} as Meeting));
      data.held_on && setHeldOn(new Date(data.held_on));
    } catch {
      toast.error('Unable to refresh meeting');
    }
  }, [meetingId]);

  const handleUpdateHeldOn = useCallback(
    async (date: Date | null) => {
      if (!meeting?.id || !date) return;

      try {
        axios.defaults.withCredentials = true;
        await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        const response = await axios.patch(
          `${config.site.api}/api/v1/meetings/${meeting.id}`,
          { held_on: date },
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );

        const data = response.data;
        if (has(data, 'error')) {
          toast.error(data.error?.message);
        }
      } catch (error: any) {
        toast.error(error?.message || 'Unable to update meeting');  
        console.error(error?.message);
      }
    },
    [meeting?.id]
  );

  const handleOpenPreview = useCallback(async (file: MeetingFile) => {
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await getS3PresignedUrlFromPath(file.key);
      setPreviewUrl(url);
    } catch {
      toast.error('Unable to load document');
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
    setPreviewUrl(null);
  }, []);

  const handleOpenInNewTab = useCallback(async (file: MeetingFile) => {
    try {
      const url = await getS3PresignedUrlFromPath(file.key);
      window.open(url, '_blank');
    } catch {
      toast.error('Unable to open document');
    }
  }, []);

  const handleUploadDocument = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f || !meeting?.id) return;
      setUploadingDoc(true);
      try {
        const { url, key } = await getPresignedUploadUrl(f);
        await uploadFileToPresignedUrl(url, f);
        const existing = (meeting.files ?? []) as MeetingFile[];
        const files = [...existing, { key, file_name: f.name }];
        axios.defaults.withCredentials = true;
        await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        await axios.patch(
          `${config.site.api}/api/v1/meetings/${meeting.id}`,
          { files },
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        toast.success('Document uploaded');
        await refetchMeeting();
      } catch (err) {
        toast.error('Failed to upload document');
      } finally {
        setUploadingDoc(false);
        e.target.value = '';
      }
    },
    [meeting?.id, meeting?.files, refetchMeeting]
  );

  useEffect(() => {
    const getMeeting = async () => {
      try {
        const response = await axios.get(`${config.site.api}/api/v1/meetings/${meetingId}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        const data = response.data;
        setMeeting(data ?? ({} as Meeting));
        data.held_on && setHeldOn(new Date(data.held_on));
      } catch (error) {
        setMeeting({} as Meeting);
        toast.error('Unable to fetch meeting');
      }
    };

    getMeeting();
  }, [meetingId]);

  return (
    <>
      {!meeting && (
        <Card sx={{ mt: 2 }}>
          <CardHeader title={<Skeleton width={240} />} />
          <Divider />
          <CardContent>
            <Skeleton height={56} sx={{ mb: 2 }} />
            <Skeleton height={320} />
          </CardContent>
        </Card>
      )}

      {meeting && Object.values(meeting).length === 0 ? (
        <Grid xs={12}>
          <Alert severity="info">No meeting found</Alert>
        </Grid>
      ) : null}

      {meeting && Object.values(meeting).length ? (
        <Card sx={{ mt: 2 }}>
          <CardHeader title="Meeting Details" />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid md={4} xs={12}>
                <DateTimePicker
                  label="Meeting Held On"
                  onChange={handleUpdateHeldOn}
                  sx={{ width: '100%' }}
                  value={heldOn}
                />
              </Grid>
              <Grid xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Meeting Agenda
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {meeting.agenda || 'No agenda set for this meeting'}
                </Typography>
              </Grid>
              <Grid xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Meeting document(s)
                </Typography>
                {meeting.files?.length ? (
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {meeting.files.map((file: MeetingFile, idx: number) => (
                      <Link
                        key={file.key + String(idx)}
                        component="button"
                        variant="body2"
                        onClick={() => handleOpenPreview(file)}
                        sx={{ display: 'block', mb: 0.5, cursor: 'pointer' }}
                      >
                        <FileArrowDown size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        {file.file_name ?? file.key}
                      </Link>
                    ))}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No document attached.
                  </Typography>
                )}
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadSimple size={18} />}
                  disabled={uploadingDoc}
                >
                  {uploadingDoc ? 'Uploading...' : 'Upload document'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.txt,image/*"
                    onChange={handleUploadDocument}
                  />
                </Button>
              </Grid>
              <Grid xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Meeting Notes & Content
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use the rich text editor below to add detailed meeting notes and content.
                </Typography>
                <MeetingTiptap meeting={meeting} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={Boolean(previewFile)}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            width: '90vw',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" noWrap sx={{ flex: 1, pr: 2 }}>
            {previewFile?.file_name ?? previewFile?.key ?? 'Document'}
          </Typography>
          <IconButton aria-label="Close" onClick={handleClosePreview} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
          {previewLoading ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Loading document…
            </Alert>
          ) : previewUrl && previewFile ? (
            (() => {
              const name = previewFile.file_name ?? previewFile.key ?? '';
              if (isPdfFileName(name)) {
                return (
                  <iframe
                    src={previewUrl}
                    title={name}
                    style={{
                      flex: 1,
                      width: '100%',
                      minHeight: 480,
                      border: 'none',
                    }}
                  />
                );
              }
              if (isImageFileName(name)) {
                return (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '75vh',
                      objectFit: 'contain',
                      m: 'auto',
                      display: 'block',
                    }}
                  />
                );
              }
              return (
                <Alert severity="info" sx={{ m: 2 }} action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<FileArrowDown size={18} />}
                    onClick={() => previewFile && handleOpenInNewTab(previewFile)}
                  >
                    Open in new tab
                  </Button>
                }>
                  This file type cannot be previewed here. Open it in a new tab to view.
                </Alert>
              );
            })()
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MeetingOverview;
